import { Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'common';
import { recipesSlice, selectRecipesGridData, updateHiddenRecipeHeaders } from 'common/src/store/recipes';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CfIconButton } from '../../../cf-ui/buttons/CfIconButton';
import { CfDialog } from '../../../cf-ui/dialogs/Dialog/CfDialog';
import { getResolution } from '../../../utilities/resolution';
import { RecipeContainedGrid } from '../../recipes/components/RecipeContainedGrid/RecipeContainedGrid';

export type HiddenRecipesDialogProps = {
  onClose: () => void;
};

const HiddenRecipesDialog: FC<HiddenRecipesDialogProps> = (props: HiddenRecipesDialogProps): JSX.Element => {
  const { onClose } = props;
  const dispatch = useAppDispatch();

  const { customerId } = useParams<{ customerId: string }>();

  const [filterText, setFilterText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [changedHiddenRecipes, setChangedHiddenRecipes] = useState<Record<string, boolean>>({});

  const recipesGridData = useAppSelector((s) =>
    selectRecipesGridData(s, filterText, undefined, undefined, undefined, undefined, true, undefined)
  );

  const handleVisibilityChange = (recipeId: string, isHidden: boolean) => {
    setChangedHiddenRecipes((prevState) => ({
      ...prevState,
      [recipeId]: isHidden,
    }));
  };

  const handleUpdate = async () => {
    const updatedHiddenRecipeList = Object.entries(changedHiddenRecipes).map(([recipeHeaderId, isHidden]) => ({
      RecipeHeaderId: recipeHeaderId,
      IsAdd: isHidden,
    }));

    if (!updatedHiddenRecipeList.length) return;

    setIsLoading(true);
    await dispatch(updateHiddenRecipeHeaders(updatedHiddenRecipeList, customerId, getResolution()));
    setIsLoading(false);
  };

  const handleClose = () => {
    dispatch(recipesSlice.actions.updateAllCategoryIsCollapsed(false));
    setChangedHiddenRecipes({});
    onClose();
  };

  return (
    <CfDialog
      title='Manage hidden recipes'
      isOpen={true}
      isLoading={isLoading}
      onClose={handleClose}
      primaryButtonProps={{
        children: 'Save',
        onClick: async () => {
          handleUpdate();
          setChangedHiddenRecipes({});
          onClose();
        },
      }}
      secondaryButtonProps={{
        children: 'Cancel',
        onClick: () => {
          handleClose();
        },
      }}
      paperSx={{
        maxWidth: 450,
      }}
    >
      <RecipeContainedGrid
        id='hidden-recipes'
        recipeHeaders={recipesGridData}
        handleFilter={setFilterText}
        filterText={filterText}
        renderActionComponentRight={(recipeHeader) => {
          const checked = changedHiddenRecipes?.[recipeHeader.RecipeHeaderId] ?? recipeHeader.IsHidden;
          return (
            <Box sx={{ ml: { xl: 1.3 } }}>
              <CfIconButton
                className={checked ? 'fa-eye-slash' : 'fa-eye'}
                disabled={!recipeHeader.IsEditable}
                onClick={() => {
                  handleVisibilityChange?.(recipeHeader.RecipeHeaderId, !checked);
                }}
              />
            </Box>
          );
        }}
      />
    </CfDialog>
  );
};

export default HiddenRecipesDialog;
